import { useCallback, useRef, useState } from 'react';
import { FormatListBulletedOutlined } from '@mui/icons-material';
import {
	ConfirmModal,
	useConfirmModal,
	useToast,
	PageHeader,
	FormCard,
	FormActions,
} from '@practium/ui';
import {
	useDeleteGroup,
	useDeleteHierarchy,
	useDeleteManagementPlan,
} from '@practium/features/api';
import type {
	SystemTab,
	SystemPageMode,
	GroupFormData,
	HierarchyFormData,
	PlanFormData,
} from '../types';
import { GroupForm } from '../components/Forms/GroupForm/GroupForm';
import type { GroupFormRef } from '../components/Forms/GroupForm/GroupForm';
import { HierarchyForm } from '../components/Forms/HierarchyForm/HierarchyForm';
import type { HierarchyFormRef } from '../components/Forms/HierarchyForm/HierarchyForm';
import { PlanForm } from '../components/Forms/PlanForm/PlanForm';
import type { PlanFormRef } from '../components/Forms/PlanForm/PlanForm';
import { SYSTEM_TITLES, SYSTEM_SUBTITLES } from '../constants/page.constants';
import styles from './AddSystemPage.module.css';

export interface AddSystemPageProps {
	id?: string;
	type: SystemTab;
	onBack: () => void;
	initialMode?: SystemPageMode;
	initialData?: GroupFormData | HierarchyFormData | PlanFormData;
	onDelete?: () => void;
	canEdit?: boolean;
}

export const AddSystemPage = ({
	id,
	type,
	onBack,
	initialMode = 'add',
	initialData,
	onDelete,
	canEdit = true,
}: AddSystemPageProps) => {
	const [currentMode, setCurrentMode] = useState<SystemPageMode>(initialMode);
	const [isLoading, setIsLoading] = useState(false);

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const groupRef = useRef<GroupFormRef>(null);
	const hierarchyRef = useRef<HierarchyFormRef>(null);
	const planRef = useRef<PlanFormRef>(null);

	const { mutateAsync: deleteGroup } = useDeleteGroup();
	const { mutateAsync: deleteHierarchy } = useDeleteHierarchy();
	const { mutateAsync: deleteManagementPlan } = useDeleteManagementPlan();

	const handleSubmit = useCallback(() => onBack(), [onBack]);

	const isFormDirty = useCallback(() => {
		if (type === 'permissions') return groupRef.current?.isDirty?.() ?? false;
		if (type === 'hierarchies') return hierarchyRef.current?.isDirty?.() ?? false;
		return planRef.current?.isDirty?.() ?? false;
	}, [type]);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			if (currentMode === 'edit' && initialMode === 'view') {
				setCurrentMode('view');
			} else {
				onBack();
			}
		};
		if (!isFormDirty()) {
			revert();
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		revert();
	}, [confirm, currentMode, initialMode, isFormDirty, onBack]);

	const { success: toastSuccess, error: toastError } = useToast();

	const handleDelete = useCallback(async () => {
		if (!id) return;
		const confirmed = await confirm({
			title: 'Excluir item',
			message: 'Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return;
		try {
			if (type === 'permissions') await deleteGroup(id);
			else if (type === 'hierarchies') await deleteHierarchy(id);
			else await deleteManagementPlan(id);
			toastSuccess('Item excluído com sucesso.');
			onDelete?.();
		} catch (err) {
			toastError(err instanceof Error ? err.message : 'Erro ao excluir item.');
		}
	}, [confirm, id, type, deleteGroup, deleteHierarchy, deleteManagementPlan, onDelete, toastSuccess, toastError]);

	const handleEdit = useCallback(() => setCurrentMode('edit'), []);

	const handleSave = useCallback(() => {
		if (type === 'permissions') groupRef.current?.submit();
		else if (type === 'hierarchies') hierarchyRef.current?.submit();
		else planRef.current?.submit();
	}, [type]);

	const sharedFormProps = {
		mode: currentMode,
		onSubmit: handleSubmit,
		onLoadingChange: setIsLoading,
	};

	const pageActions = (
		<div className={styles.pageActions}>
			<FormActions
				mode={currentMode}
				onCancel={handleCancel}
				onSave={handleSave}
				isLoading={isLoading}
				onDelete={onDelete ? handleDelete : undefined}
				onEdit={canEdit ? handleEdit : undefined}
			/>
		</div>
	);

	const confirmModal = (
		<ConfirmModal
			isOpen={isConfirmOpen}
			title={confirmOptions.title}
			message={confirmOptions.message}
			confirmText={confirmOptions.confirmText}
			cancelText={confirmOptions.cancelText}
			variant={confirmOptions.variant}
			onConfirm={handleConfirm}
			onCancel={handleConfirmCancel}
		/>
	);

	return (
		<div className={styles.page}>
			<div className={styles.content}>
				<PageHeader
					title={SYSTEM_TITLES[type][currentMode]}
					subtitle={SYSTEM_SUBTITLES[currentMode][type]}
					onBack={onBack}
				/>
				<FormCard icon={<FormatListBulletedOutlined />} title="Informações Gerais">
					{type === 'permissions' && (
						<GroupForm
							ref={groupRef}
							id={id}
							{...sharedFormProps}
							initialData={initialData as GroupFormData | undefined}
						/>
					)}
					{type === 'hierarchies' && (
						<HierarchyForm
							ref={hierarchyRef}
							id={id}
							{...sharedFormProps}
							initialData={initialData as HierarchyFormData | undefined}
						/>
					)}
					{type === 'plans' && (
						<PlanForm
							ref={planRef}
							id={id}
							{...sharedFormProps}
							initialData={initialData as PlanFormData | undefined}
						/>
					)}
				</FormCard>
				{pageActions}
				{confirmModal}
			</div>
		</div>
	);
};
